import { openLocalDb } from '../db/local.js';

// Successful HTTP transport does not mean every operation was accepted.
// Inspect durable outboxes; never mutate, drop or reprint an operation here.
export async function readQueueAttentionCount(tenantId: string, now = Date.now()): Promise<number> {
  const db = await openLocalDb(null);
  const staleBefore = new Date(now - 5 * 60_000).toISOString();
  const result = await db.query(
    `SELECT count(*) AS count FROM (
       SELECT status, attempts, last_error, queued_at FROM pending_stock_commands WHERE tenant_id = ?
       UNION ALL
       SELECT status, attempts, last_error, queued_at FROM pending_atomic_rpcs_v1 WHERE tenant_id = ?
       UNION ALL
       SELECT status, attempts, last_error, queued_at FROM pending_label_print_outcomes_v1 WHERE tenant_id = ?
       UNION ALL
       SELECT 'pending', attempts, last_error, created_at FROM pending_ops
        WHERE CASE WHEN json_valid(payload) THEN
          coalesce(json_extract(payload, '$.tenant_id'), json_extract(payload, '$.patch.tenant_id'))
        ELSE NULL END = ?
       UNION ALL
       SELECT 'conflict', attempts, reason, created_at FROM pending_op_reconciliations_v1
        WHERE status = 'reconciliation_required'
          AND coalesce(json_extract(payload_json, '$.tenant_id'), json_extract(payload_json, '$.patch.tenant_id')) = ?
       UNION ALL
       SELECT 'conflict', 0, reason, created_at FROM stock_command_reconciliations_v1
        WHERE status = 'reconciliation_required' AND tenant_id = ?
     ) WHERE status = 'conflict'
          OR last_error IS NOT NULL
          OR julianday(queued_at) <= julianday(?)`,
    [tenantId, tenantId, tenantId, tenantId, tenantId, tenantId, staleBefore],
  );
  return Number(result.values?.[0]?.count ?? 0);
}

/** Candidates only: the enqueue overlap guard still protects races. */
export async function listUnownedRepairMasters(table: string, tenantId: string): Promise<Array<{ id: string; row: string }>> {
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT m.id, m.row FROM masters_local m
      WHERE m.table_name = ? AND m.tenant_id = ? AND m.archived = 0
        AND NOT EXISTS (SELECT 1 FROM pending_stock_command_master_snapshots s
          WHERE s.table_name = m.table_name AND s.id = m.id)
        AND NOT EXISTS (SELECT 1 FROM pending_atomic_rpc_master_snapshots_v1 s
          JOIN pending_atomic_rpcs_v1 r ON r.request_id = s.request_id
          WHERE s.table_name = m.table_name AND s.id = m.id)
        AND NOT EXISTS (SELECT 1 FROM pending_ops p WHERE p.kind = 'master'
          AND json_extract(p.payload, '$.table') = m.table_name
          AND json_extract(p.payload, '$.id') = m.id)
        AND NOT EXISTS (SELECT 1 FROM pending_op_reconciliations_v1 p
          WHERE p.kind = 'master' AND p.status = 'reconciliation_required'
          AND json_extract(p.payload_json, '$.table') = m.table_name
          AND json_extract(p.payload_json, '$.id') = m.id)
        AND NOT EXISTS (SELECT 1 FROM stock_command_reconciliations_v1 r,
          json_each(r.master_effects_json) effect
          WHERE r.status = 'reconciliation_required' AND r.tenant_id = m.tenant_id
          AND json_extract(effect.value, '$.table') = m.table_name
          AND json_extract(effect.value, '$.id') = m.id)`,
    [table, tenantId],
  );
  return (result.values ?? []) as Array<{ id: string; row: string }>;
}

/** Bounded diagnostics: no business payloads, credentials or automatic repairs. */
export async function readQueueAttentionDetails(tenantId: string, now = Date.now()): Promise<Array<{
  queue: string; operation_id: string; total: number; reason: string | null; queued_at: string;
}>> {
  const db = await openLocalDb(null);
  const result = await db.query(
    `WITH attention AS (
       SELECT 'stock_commands' AS queue, command_id AS operation_id, status, last_error AS reason, queued_at
         FROM pending_stock_commands WHERE tenant_id = ?
       UNION ALL SELECT 'atomic_rpcs', request_id, status, last_error, queued_at
         FROM pending_atomic_rpcs_v1 WHERE tenant_id = ?
       UNION ALL SELECT 'label_outcomes', intent_id, status, last_error, queued_at
         FROM pending_label_print_outcomes_v1 WHERE tenant_id = ?
       UNION ALL SELECT 'legacy_ops', coalesce(json_extract(payload, '$.client_uuid'), json_extract(payload, '$.id'), CAST(id AS TEXT)), 'pending', last_error, created_at FROM pending_ops
         WHERE CASE WHEN json_valid(payload) THEN
           coalesce(json_extract(payload, '$.tenant_id'), json_extract(payload, '$.patch.tenant_id'))
         ELSE NULL END = ?
       UNION ALL SELECT 'legacy_reconciliations', coalesce(json_extract(payload_json, '$.client_uuid'), json_extract(payload_json, '$.id'), CAST(id AS TEXT)), 'conflict', reason, created_at
         FROM pending_op_reconciliations_v1 WHERE status = 'reconciliation_required'
         AND coalesce(json_extract(payload_json, '$.tenant_id'), json_extract(payload_json, '$.patch.tenant_id')) = ?
       UNION ALL SELECT 'stock_reconciliations', command_id, 'conflict', reason, created_at
         FROM stock_command_reconciliations_v1 WHERE status = 'reconciliation_required' AND tenant_id = ?
     ), ranked AS (
       SELECT queue, operation_id, count(*) OVER(PARTITION BY queue) AS total,
              substr(reason, 1, 200) AS reason, queued_at,
              row_number() OVER(PARTITION BY queue ORDER BY queued_at, operation_id) AS rank
         FROM attention WHERE status = 'conflict' OR reason IS NOT NULL OR julianday(queued_at) <= julianday(?)
     ) SELECT queue, operation_id, total, reason, queued_at FROM ranked WHERE rank <= 5 ORDER BY queue, rank`,
    [tenantId, tenantId, tenantId, tenantId, tenantId, tenantId, new Date(now - 5 * 60_000).toISOString()],
  );
  return (result.values ?? []) as Array<{
    queue: string; operation_id: string; total: number; reason: string | null; queued_at: string;
  }>;
}
